import type { ReplayTimeline } from './buildTimeline';
import { readLines } from './readLines';
import { ReplayTimelineBuilder } from './streamTimeline';

export type ReplayStreamMessage =
  | { type: 'progress'; bytes: number }
  | { type: 'building' }
  | { type: 'done'; timeline: ReplayTimeline }
  | { type: 'error'; message: string };

export interface ReplayLoad {
  promise: Promise<ReplayTimeline>;
  cancel: () => void;
}

/**
 * Load a finished recording's replay timeline from its presigned URL,
 * streamed (see streamTimeline.ts). In a browser the work runs in a Web
 * Worker; where there is none (tests, very old browsers) it runs in-thread
 * with the same code. `onProgress` gets the bytes read; `onBuilding` fires
 * once the whole file is read and the timeline is being computed.
 */
export function loadReplayTimeline(
  url: string,
  { onProgress, onBuilding }: { onProgress?: (bytes: number) => void; onBuilding?: () => void } = {},
): ReplayLoad {
  if (typeof Worker === 'undefined') {
    let cancelled = false;
    const promise = (async () => {
      const resp = await fetch(url);
      if (!resp.ok) throw new Error(`download failed (${resp.status})`);
      const builder = new ReplayTimelineBuilder();
      const finished = await readLines(resp, (l) => builder.feedLine(l), onProgress, () => cancelled);
      if (!finished) throw new Error('cancelled');
      onBuilding?.();
      return builder.finish();
    })();
    return { promise, cancel: () => { cancelled = true; } };
  }
  const worker = new Worker(new URL('./replayStream.worker.ts', import.meta.url), {
    type: 'module',
  });
  const promise = new Promise<ReplayTimeline>((resolve, reject) => {
    worker.onmessage = (e: MessageEvent<ReplayStreamMessage>) => {
      const m = e.data;
      if (m.type === 'progress') onProgress?.(m.bytes);
      else if (m.type === 'building') onBuilding?.();
      else if (m.type === 'done') {
        resolve(m.timeline);
        worker.terminate();
      } else {
        reject(new Error(m.message));
        worker.terminate();
      }
    };
    worker.onerror = () => {
      reject(new Error('Could not load this recording for replay.'));
      worker.terminate();
    };
  });
  worker.postMessage({ url });
  return { promise, cancel: () => worker.terminate() };
}
